import { isNil, isPlainObject, isString } from 'lodash';

/**
 * Password-reset JWTs issued by Neutron.
 * - Inner token from `jwt.encode(user.id, passwordResetSecret)` (≈36.51.43).
 * - Outer token from `jwtService.sign({ secretEmailAddress, resetToken })` used by
 *   `generateVerificationLink` / `verifyAndResetPassword` (variable-length claims).
 * Header/payload use base64url (`A–Z`, `a–z`, `0–9`, `_`, `-`) without `=` so
 * `token=` prefixes are preserved; signature also allows `+`, `/`, `=`.
 *
 * Must start with `eyJ` (base64url of a JSON header) so digests like
 * `sha256:0000….1111….2222…` are not treated as JWTs.
 *
 * Upper bounds on each segment avoid quadratic backtracking on long undotted runs
 * (e.g. data URIs in Helios RUM resource URLs) — unbounded `{30,}` was multi-second
 * on 40k-char strings on the main thread.
 */
const PASSWORD_RESET_JWT_PATTERN =
  /eyJ[a-zA-Z0-9_-]{27,509}\.[a-zA-Z0-9_-]{40,512}\.[a-zA-Z0-9_\-+/=]{30,512}/gu;

/**
 * Email + token (or token alone) in auth reset / verify-and-reset route paths.
 * Matches Luna/Helios/Circe frontend routes and Neutron POST paths that embed the JWT
 * (with or without a leading slash — axios often uses relative `auth/reset-password/...`).
 */
const AUTH_RESET_PATH_PATTERN =
  /(?<pathPrefix>\/?auth\/(?:verify-and-)?reset-password\/)(?<firstSegment>[^/?#]+)(?:\/(?<secondSegment>[^/?#]+))?/giu;

/** Cap nested walk depth so pathological trees cannot hang telemetry hooks. */
export const REDACT_SENSITIVE_DATA_MAX_DEPTH = 32;

export const JWT_REDACTED = 'JWT_REDACTED';
export const EMAIL_REDACTED = 'EMAIL_REDACTED';

/**
 * Redacts password-reset JWTs and emails embedded in auth reset URLs from telemetry strings.
 * Safe to call on any string that may appear in Sentry/Datadog fields (URL, transaction, stack).
 * Non-string / nil / empty values are returned unchanged (typed as the input).
 */
export function redactSensitiveData<T>(value: T): T {
  if (isNil(value) || !isString(value) || value.length === 0) {
    return value;
  }

  let result = value.replace(AUTH_RESET_PATH_PATTERN, (matchedText, ...replaceArgs) => {
    void matchedText;
    const groups = replaceArgs[replaceArgs.length - 1] as {
      pathPrefix?: string;
      firstSegment?: string;
      secondSegment?: string;
    };
    const pathPrefix = groups.pathPrefix ?? '';
    const secondSegment = groups.secondSegment;

    if (isString(secondSegment) && secondSegment.length > 0) {
      return `${pathPrefix}${EMAIL_REDACTED}/${JWT_REDACTED}`;
    }
    // Single segment after reset-password/ — treat as token (API POST or already email-stripped).
    return `${pathPrefix}${JWT_REDACTED}`;
  });

  result = result.replace(PASSWORD_RESET_JWT_PATTERN, JWT_REDACTED);

  return result as T;
}

/**
 * Deep-redacts string leaves in a plain object/array/Map/Set tree (Sentry/DD payloads).
 *
 * Builds a **new** tree — never mutates `input` — so a throw cannot leave half-redacted state.
 * Cycle-safe via WeakMap (copies point at the in-progress clone). Depth-capped.
 *
 * Returns `null` on any failure (depth exceeded, throwing getter, unexpected error). Callers
 * must treat `null` as fail-closed and drop the telemetry event rather than send the original.
 */
export function redactSensitiveDataDeep<T>(input: T): T | null {
  try {
    return walkRedact(input, new WeakMap<object, unknown>(), 0) as T;
  } catch {
    return null;
  }
}

function walkRedact(input: unknown, copies: WeakMap<object, unknown>, depth: number): unknown {
  if (isNil(input)) {
    return input;
  }

  if (isString(input)) {
    return redactSensitiveData(input);
  }

  if (typeof input !== 'object') {
    return input;
  }

  // typeof null === 'object'; isNil already returned above — narrow for WeakMap.
  const objectInput = input as object;

  if (depth > REDACT_SENSITIVE_DATA_MAX_DEPTH) {
    throw new RangeError('redactSensitiveDataDeep: max depth exceeded');
  }

  const existingCopy = copies.get(objectInput);
  if (existingCopy !== undefined) {
    return existingCopy;
  }

  if (Array.isArray(input)) {
    const copy: unknown[] = [];
    copies.set(objectInput, copy);
    for (let index = 0; index < input.length; index += 1) {
      copy[index] = walkRedact(input[index], copies, depth + 1);
    }
    return copy;
  }

  if (input instanceof Map) {
    const copy = new Map();
    copies.set(objectInput, copy);
    for (const [key, value] of input.entries()) {
      copy.set(walkRedact(key, copies, depth + 1), walkRedact(value, copies, depth + 1));
    }
    return copy;
  }

  if (input instanceof Set) {
    const copy = new Set();
    copies.set(objectInput, copy);
    for (const value of input.values()) {
      copy.add(walkRedact(value, copies, depth + 1));
    }
    return copy;
  }

  // Date, Error, class instances, buffers, etc.: leave the original reference.
  // Walking them via Object.keys either drops semantics or hits host cycles.
  if (!isPlainObject(input)) {
    return input;
  }

  const copy: Record<string, unknown> = {};
  copies.set(objectInput, copy);
  const record = input as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    copy[key] = walkRedact(record[key], copies, depth + 1);
  }
  return copy;
}
